export const SOUND_EFFECT_MODEL = 'bytedance/seed-audio-1.0';
export const SOUND_EFFECT_MAX_PROMPT_LENGTH = 2000;
export const SOUND_EFFECT_DRAFT_KEY = 'soundEffectDraft';

export type SoundEffectFormat = 'wav' | 'mp3';

export interface SoundEffectRequest {
  prompt: string;
  /** A prompt instruction, not an exact length guarantee. */
  duration?: number;
  output_format?: SoundEffectFormat;
}

export function parseSoundEffectRequest(
  body: unknown,
): Required<SoundEffectRequest> {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    throw new Error('Invalid request body');
  }
  const input = body as Record<string, unknown>;
  if (typeof input.prompt !== 'string' || !input.prompt.trim()) {
    throw new Error('Describe the sound effect you want to create.');
  }
  const prompt = input.prompt.trim();
  if (prompt.length > SOUND_EFFECT_MAX_PROMPT_LENGTH) {
    throw new Error(
      `Keep your description within ${SOUND_EFFECT_MAX_PROMPT_LENGTH} characters.`,
    );
  }
  // Keep numeric strings compatible with existing API consumers.
  const duration =
    input.duration === undefined
      ? 8
      : typeof input.duration === 'number' ||
          (typeof input.duration === 'string' && input.duration.trim())
        ? Number(input.duration)
        : NaN;
  if (!Number.isFinite(duration) || duration < 1 || duration > 30) {
    throw new Error('Target duration must be between 1 and 30 seconds.');
  }
  const output_format = input.output_format ?? 'wav';
  if (output_format !== 'wav' && output_format !== 'mp3') {
    throw new Error('Choose WAV or MP3 as the output format.');
  }
  return { prompt, duration, output_format };
}

export function buildSoundEffectPrompt({
  prompt,
  duration,
}: Required<SoundEffectRequest>) {
  return `Create sound effects and ambience only. No speech, narration, singing or music.\n${prompt}\nAim for approximately ${duration} seconds, with a natural ending.`;
}
